import { useEffect, useState } from "react";
import SEO from "../../components/SEO";
import FormAction from "../../components/FormAction";
import banner from "../../assets/img/asset7.png";
import { SlCheck } from "react-icons/sl";
import { Disclosure, Transition } from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { Dropdown } from "primereact/dropdown";
import {
  BsCheckCircle,
  BsTruck,
  BsShieldCheck,
  BsClock,
  BsExclamationTriangleFill,
  BsLightningCharge,
  BsMoonStars,
  BsCalendarCheck,
  BsArrowRight,
} from "react-icons/bs";
import { useNavigate } from "react-router-dom";
import ProvinceService from "../../services/province.service";
import TownService from "../../services/town.service";
import ParcelService from "../../services/parcel.service";
import AddressInput from "../../components/AddressInput";

// Add your clients here. Logos go in src/assets/clients/
// Shape: { name: string, logo: imported_image, link: string }
const trustedClients = [
  // Example (uncomment and replace when you have logos):
  {
    name: "Khanyisa A Wellness",
    logo: require("../../assets/clients/kaw.jpg"),
    link: "https://www.khanyisaawellness.co.za/",
  },
  {
    name: "Tshovelo Business Solutions",
    logo: require("../../assets/clients/tbs.jpg"),
    link: "https://www.tshovelo.co.za/",
  },
  {
    name: "Mahera Beauty",
    logo: require("../../assets/clients/mahera.jpg"),
    link: "https://web.facebook.com/MAHERABEAUTY",
  },
{
    name: "Lass Skincare",
    logo: require("../../assets/clients/lass.png"),
    link: "https://www.lassskincare.co.za/",
  },
  {
    name: "Guru Nation",
    logo: require("../../assets/clients/guru-nation.png"),
    link: "https://guru-nation.lovable.app/",
  },
];

const ClientCard = ({ client }) => (
  <a
    href={client.link}
    target="_blank"
    rel="noopener noreferrer"
    className="group flex flex-col items-center gap-3"
  >
    <div className="bg-white rounded-xl p-4 shadow-sm transition-transform group-hover:scale-105">
      <img
        src={client.logo}
        alt={client.name}
        className="h-16 w-36 object-contain"
        onError={(e) => {
          e.target.style.display = "none";
          e.target.nextSibling.style.display = "block";
        }}
      />
      <span className="hidden text-sm font-semibold text-gray-600">{client.name}</span>
    </div>
    <span className="text-xs font-medium text-gray-400 tracking-wide uppercase group-hover:text-purple-500 transition-colors">
      {client.name}
    </span>
  </a>
);

export const HomePage = () => {
  const [createOrderState, setCreateOrderState] = useState({
    distance_type: "long", // "short" or "long"
    distance: "long", // distance state field
    departure_province: "",
    destination_province: "",
    departure_town: "",
    destination_town: "",
    departure_address: "",
    destination_address: "",
    parcel_type: "",
    delivery_type: "",
    provinces: [],
    destination_provinces: [],
    departure_towns: [],
    destination_towns: [],
    parcels: [],
    start_prices: ["60", "100", "150"],
    showPackageError: false,
    acceptedProhibitedItems: false,
  });

  const [errors, setErrors] = useState({
    departure_province: "",
    destination_province: "",
    departure_town: "",
    destination_town: "",
    departure_address: "",
    destination_address: "",
    acceptedProhibitedItems: "",
  });

  const [addressTouched, setAddressTouched] = useState({
    departure_address: false,
    destination_address: false,
  });

  const [distanceWarning, setDistanceWarning] = useState({
    departure: "",
    destination: "",
  });

  const [showValidationSummary, setShowValidationSummary] = useState(false);

  const {
    distance_type,
    distance,
    departure_province,
    destination_province,
    departure_town,
    destination_town,
    departure_address,
    destination_address,
    parcel_type,
    delivery_type,
    provinces,
    destination_provinces,
    departure_towns,
    destination_towns,
    parcels,
    start_prices,
    showPackageError,
    acceptedProhibitedItems,
  } = createOrderState;

  const navigate = useNavigate();

  // Gallagher Business Exchange coordinates (Parcelin Midrand warehouse)
  const MIDRAND_COORDS = { lat: -25.9856, lng: 28.1289 };
  const MAX_DISTANCE_KM = 70;

  // Calculate distance using Google Distance Matrix API
  const calculateDistanceGoogle = async (address) => {
    try {
      const service = new window.google.maps.DistanceMatrixService();

      return new Promise((resolve, reject) => {
        service.getDistanceMatrix(
          {
            origins: [`${MIDRAND_COORDS.lat},${MIDRAND_COORDS.lng}`],
            destinations: [address],
            travelMode: window.google.maps.TravelMode.DRIVING,
            unitSystem: window.google.maps.UnitSystem.METRIC,
            avoidHighways: false,
            avoidTolls: false,
          },
          (response, status) => {
            if (status === window.google.maps.DistanceMatrixStatus.OK) {
              const element = response.rows[0].elements[0];
              if (element.status === "OK") {
                const distanceKm = element.distance.value / 1000; // Convert meters to km
                resolve(distanceKm);
              } else {
                reject(new Error("Could not calculate distance"));
              }
            } else {
              reject(new Error("Distance Matrix API error"));
            }
          },
        );
      });
    } catch (error) {
      throw error;
    }
  };

  // Fallback: Calculate distance between two coordinates using Haversine formula
  const calculateDistanceHaversine = (lat1, lon1, lat2, lon2) => {
    const R = 6371; // Earth's radius in km
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  // Validate address distance from Midrand
  const validateAddressDistance = async (address, fieldType) => {
    if (!address || address.trim() === "") {
      setDistanceWarning((prev) => ({
        ...prev,
        [fieldType]: "",
      }));
      return true;
    }

    try {
      let distance;

      // Try Google Distance Matrix API first (more accurate for driving distances)
      if (window.google && window.google.maps) {
        try {
          distance = await calculateDistanceGoogle(address);
        } catch (googleError) {
          console.warn(
            "Google Distance Matrix failed, falling back to geocoding:",
            googleError,
          );
          // Fallback to geocoding + Haversine
          distance = await calculateDistanceFallback(address);
        }
      } else {
        // Google Maps not loaded, use fallback
        distance = await calculateDistanceFallback(address);
      }

      if (distance > MAX_DISTANCE_KM) {
        const warningMsg = `This address is approximately ${Math.round(distance)}km from our Midrand warehouse (Gallagher Business Exchange, Halfway House). We currently only service areas within ${MAX_DISTANCE_KM}km of Midrand.`;
        setDistanceWarning((prev) => ({
          ...prev,
          [fieldType]: warningMsg,
        }));
        return false;
      } else {
        setDistanceWarning((prev) => ({
          ...prev,
          [fieldType]: "",
        }));
        return true;
      }
    } catch (error) {
      console.error("Error validating address distance:", error);
      // Don't block the user if distance calculation fails
      setDistanceWarning((prev) => ({
        ...prev,
        [fieldType]: "",
      }));
      return true;
    }
  };

  // Fallback distance calculation using geocoding + Haversine
  const calculateDistanceFallback = async (address) => {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address)}&countrycodes=za&limit=1`,
    );
    const data = await response.json();

    if (data && data.length > 0) {
      const { lat, lon } = data[0];
      return calculateDistanceHaversine(
        MIDRAND_COORDS.lat,
        MIDRAND_COORDS.lng,
        parseFloat(lat),
        parseFloat(lon),
      );
    } else {
      throw new Error("Could not geocode address");
    }
  };

  const handleSelectChange = (event) => {
    const { id, value } = event.target;

    if (id === "departure_province") {
      setCreateOrderState((prevState) => ({
        ...prevState,
        ["departure_town"]: "",
        ["destination_province"]: "",
        ["destination_town"]: "",
        ["destination_towns"]: [],
      }));
    }

    setCreateOrderState((prevState) => ({
      ...prevState,
      [id]: value,
    }));
  };

  const handleRadioButtonChange = (event) => {
    const { name, value } = event;

    const updates = {
      [name]: value,
      showPackageError: false,
    };

    if (name === "parcel_type") {
      const selectedParcel = parcels.find((p) => p.id === value);
      if (
        selectedParcel &&
        selectedParcel.parcel_prices &&
        selectedParcel.parcel_prices.length > 0
      ) {
        const sortedPrices = [...selectedParcel.parcel_prices].sort(
          (a, b) => a.price.value - b.price.value,
        );
        updates.delivery_type = sortedPrices[0].price.id;
      }
    }

    setCreateOrderState((prevState) => ({
      ...prevState,
      ...updates,
    }));
  };

  useEffect(() => {
    const permittedTowns = ["Giyani", "Malamulele", "Tzaneen", "Polokwane", "Bungeni", "Thohoyandou", "Phalaborwa"];
    const fetchProvinces = async () => {
      try {
        const data = await ProvinceService.index();
        setCreateOrderState((prevState) => ({
          ...prevState,
          provinces: data,
        }));
      } catch (error) {
        console.error("Error fetching provinces:", error);
      }
    };

    const fetchParcels = async () => {
      try {
        const data = await ParcelService.index(distance);
        setCreateOrderState((prevState) => ({
          ...prevState,
          parcels: data,
        }));
      } catch (error) {
        console.error("Error fetching parcel types:", error);
      }
    };

    const fetchDestinationProvinces = async () => {
      try {
        if (departure_province) {
          const data =
            await ProvinceService.excluding_province(departure_province);
          setCreateOrderState((prevState) => ({
            ...prevState,
            destination_provinces: data,
          }));
        }
      } catch (error) {
        console.error("Error fetching provinces:", error);
      }
    };

    const fetchDepartureTowns = async () => {
      try {
        if (distance_type === "short") {
          // For short distance, fetch Gauteng towns (assuming Gauteng province_id is 1)
          const data = await TownService.by_province("1");
          setCreateOrderState((prevState) => ({
            ...prevState,
            departure_towns: data,
            destination_towns: data, // Same towns for both departure and destination
          }));
        } else if (departure_province) {
          const data = await TownService.by_province(departure_province);
          let filtered_towns =
            departure_province.toString() == "2"
              ? data.filter((town) => permittedTowns.includes(town.name))
              : data;
          setCreateOrderState((prevState) => ({
            ...prevState,
            departure_towns: filtered_towns,
          }));
        }
      } catch (error) {
        console.error("Error fetching options for Select 2:", error);
      }
    };

    const fetchDestinationTowns = async () => {
      try {
        if (distance_type === "short") {
          // Already handled in fetchDepartureTowns for short distance
          return;
        }
        if (destination_province) {
          const data = await TownService.by_province(destination_province);
          let filtered_towns =
            destination_province.toString() == "2"
              ? data.filter((town) => permittedTowns.includes(town.name))
              : data;

          setCreateOrderState((prevState) => ({
            ...prevState,
            destination_towns: filtered_towns,
          }));
        }
      } catch (error) {
        console.error("Error fetching options for Select 2:", error);
      }
    };

    fetchProvinces();
    fetchDepartureTowns();
    fetchParcels();
    fetchDestinationProvinces();
    fetchDestinationTowns();
  }, [departure_province, destination_province, distance_type, distance]);

  const handleSubmit = (e) => {
    e.preventDefault();

    // Validate all fields
    const validationErrors = {};

    if (distance_type === "long") {
      if (!departure_province) {
        validationErrors.departure_province =
          "Please select departure province";
      }
      if (!destination_province) {
        validationErrors.destination_province =
          "Please select destination province";
      }
      if (!departure_town) {
        validationErrors.departure_town = "Please select departure town";
      }
      if (!destination_town) {
        validationErrors.destination_town = "Please select destination town";
      }
    } else {
      // Short distance validation
      if (!departure_address || departure_address.trim() === "") {
        validationErrors.departure_address = "Please enter pickup address";
      }
      if (!destination_address || destination_address.trim() === "") {
        validationErrors.destination_address = "Please enter delivery address";
      }

      // Check if there are distance warnings
      if (distanceWarning.departure || distanceWarning.destination) {
        validationErrors.distance =
          "One or more addresses are outside our service area";
      }
    }

    if (!parcel_type) {
      validationErrors.parcel_type = "Please select a package type";
    }

    if (!acceptedProhibitedItems) {
      validationErrors.acceptedProhibitedItems =
        "You must confirm that you are not sending prohibited items";
    }

    // If there are errors, show them and don't submit
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setShowValidationSummary(true);

      if (validationErrors.parcel_type) {
        setCreateOrderState((prevState) => ({
          ...prevState,
          showPackageError: true,
        }));
      }

      // Scroll to first error
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    // Clear errors and proceed
    setErrors({});
    setShowValidationSummary(false);
    createOrder();
  };

  const createOrder = () => {
    const formData = {
      distance_type,
      distance,
      departure_province: distance_type === "short" ? "1" : departure_province, // Gauteng ID
      destination_province:
        distance_type === "short" ? "1" : destination_province,
      departure_town,
      destination_town,
      departure_address,
      destination_address,
      // For short distance, pass addresses as pickup/drop-off locations
      pickup_location: distance_type === "short" ? departure_address : "",
      drop_off_location: distance_type === "short" ? destination_address : "",
      parcel_type,
      delivery_type,
      provinces,
      departure_towns,
      destination_towns,
      parcels,
    };
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
    navigate("/cart", { state: formData });
  };

  return (
    <div className="w-full flex flex-col bg-gradient-to-br from-cyan-100 to-fuchsia-200">
      <SEO path="/" />
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-5 py-16 lg:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Column - Content */}
            <div className="text-center lg:text-left space-y-6">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-purple-800 leading-tight">
                Parcel Delivery, Done Right
              </h1>
              <p className="text-lg md:text-xl text-purple-700">
                Affordable, eco-friendly parcel delivery across South Africa.
                Same-day and next-day options available.
              </p>

              {/* Quick Stats */}
              <div className="grid grid-cols-2 gap-4 pt-4">
                <div className="bg-white/70 backdrop-blur rounded-xl p-4 text-center">
                  <div className="text-2xl font-bold text-purple-600">60%</div>
                  <div className="text-sm text-gray-700">Cheaper</div>
                </div>
                <div className="bg-white/70 backdrop-blur rounded-xl p-4 text-center">
                  <div className="text-2xl font-bold text-purple-600">
                    Same Day
                  </div>
                  <div className="text-sm text-gray-700">Delivery</div>
                </div>
              </div>
            </div>

            {/* Right Column - Booking Form */}
            <div className="w-full">
              <form
                className="bg-white rounded-2xl shadow-2xl overflow-hidden"
                onSubmit={handleSubmit}
              >
                {/* Gradient header band */}
                <div className="bg-gradient-to-br from-purple-700 via-purple-600 to-violet-500 px-6 py-5 relative overflow-hidden">
                  <div className="absolute -top-6 -right-6 w-28 h-28 bg-white/10 rounded-full" />
                  <div className="absolute -bottom-8 -left-4 w-20 h-20 bg-white/5 rounded-full" />
                  <h2 className="text-2xl font-extrabold text-white relative">
                    Get Instant Quote
                  </h2>
                  <p className="text-purple-200 text-sm mt-0.5 relative">
                    Takes under 60 seconds · No signup needed
                  </p>
                </div>
                <div className="p-6 space-y-5">
                  {/* Distance Type Toggle — Segmented Control */}
                  <div className="flex rounded-xl bg-gray-100 p-1 gap-1">
                    <button
                      type="button"
                      onClick={() => {
                        if (distance_type === "long") return;
                        setCreateOrderState((prevState) => ({
                          ...prevState,
                          distance_type: "long",
                          distance: "long",
                          departure_province: "",
                          destination_province: "",
                          departure_town: "",
                          destination_town: "",
                          departure_address: "",
                          destination_address: "",
                        }));
                        setErrors({});
                        setShowValidationSummary(false);
                        setAddressTouched({
                          departure_address: false,
                          destination_address: false,
                        });
                        setDistanceWarning({ departure: "", destination: "" });
                      }}
                      className={`flex-1 py-2.5 text-sm font-semibold rounded-lg transition-all duration-200 ${
                        distance_type === "long"
                          ? "bg-white shadow text-purple-700"
                          : "text-gray-500 hover:text-gray-700"
                      }`}
                    >
                      Long Distance
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        if (distance_type === "short") return;
                        setCreateOrderState((prevState) => ({
                          ...prevState,
                          distance_type: "short",
                          distance: "short",
                          departure_province: "1",
                          destination_province: "1",
                          departure_town: "",
                          destination_town: "",
                          departure_address: "",
                          destination_address: "",
                        }));
                        setErrors({});
                        setShowValidationSummary(false);
                        setAddressTouched({
                          departure_address: false,
                          destination_address: false,
                        });
                        setDistanceWarning({ departure: "", destination: "" });
                      }}
                      className={`flex-1 py-2.5 text-sm font-semibold rounded-lg transition-all duration-200 ${
                        distance_type === "short"
                          ? "bg-white shadow text-purple-700"
                          : "text-gray-500 hover:text-gray-700"
                      }`}
                    >
                      Short Distance
                    </button>
                  </div>

                  {/* Validation Summary */}
                  {showValidationSummary && Object.keys(errors).length > 0 && (
                    <div className="bg-red-50 border-2 border-red-500 rounded-lg p-4 animate-slideDown">
                      <div className="flex items-start gap-3">
                        <BsExclamationTriangleFill
                          className="text-red-500 mt-1 flex-shrink-0"
                          size={20}
                        />
                        <div>
                          <h3 className="font-bold text-red-800 mb-1">
                            Please complete all required fields
                          </h3>
                          <ul className="text-sm text-red-700 list-disc list-inside">
                            {Object.entries(errors).map(
                              ([field, error]) =>
                                error && <li key={field}>{error}</li>,
                            )}
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Long Distance Form */}
                  {distance_type === "long" && (
                    <>
                      {/* From/To Section */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            From <span className="text-red-500">*</span>
                          </label>
                          <div
                            className={
                              errors.departure_province
                                ? "ring-2 ring-red-500 rounded-lg"
                                : ""
                            }
                          >
                            <Dropdown
                              value={departure_province}
                              onChange={(e) => {
                                handleSelectChange({
                                  target: {
                                    id: "departure_province",
                                    value: e.value,
                                  },
                                });
                                setErrors((prev) => ({
                                  ...prev,
                                  departure_province: "",
                                }));
                                setShowValidationSummary(false);
                              }}
                              options={provinces}
                              optionLabel="name"
                              optionValue="id"
                              placeholder="Province *"
                              filter
                              className="w-full"
                              style={{ borderRadius: "0.5rem" }}
                            />
                          </div>
                          {errors.departure_province && (
                            <p className="text-red-600 text-xs mt-1">
                              {errors.departure_province}
                            </p>
                          )}
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            To <span className="text-red-500">*</span>
                          </label>
                          <div
                            className={
                              errors.destination_province
                                ? "ring-2 ring-red-500 rounded-lg"
                                : ""
                            }
                          >
                            <Dropdown
                              value={destination_province}
                              onChange={(e) => {
                                handleSelectChange({
                                  target: {
                                    id: "destination_province",
                                    value: e.value,
                                  },
                                });
                                setErrors((prev) => ({
                                  ...prev,
                                  destination_province: "",
                                }));
                                setShowValidationSummary(false);
                              }}
                              options={destination_provinces}
                              optionLabel="name"
                              optionValue="id"
                              placeholder="Province *"
                              filter
                              className="w-full"
                              style={{ borderRadius: "0.5rem" }}
                            />
                          </div>
                          {errors.destination_province && (
                            <p className="text-red-600 text-xs mt-1">
                              {errors.destination_province}
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <div
                            className={
                              errors.departure_town
                                ? "ring-2 ring-red-500 rounded-lg"
                                : ""
                            }
                          >
                            <Dropdown
                              value={departure_town}
                              onChange={(e) => {
                                handleSelectChange({
                                  target: {
                                    id: "departure_town",
                                    value: e.value,
                                  },
                                });
                                setErrors((prev) => ({
                                  ...prev,
                                  departure_town: "",
                                }));
                                setShowValidationSummary(false);
                              }}
                              options={departure_towns}
                              optionLabel="name"
                              optionValue="id"
                              placeholder="Town *"
                              filter
                              className="w-full"
                              style={{ borderRadius: "0.5rem" }}
                            />
                          </div>
                          {errors.departure_town && (
                            <p className="text-red-600 text-xs mt-1">
                              {errors.departure_town}
                            </p>
                          )}
                        </div>
                        <div>
                          <div
                            className={
                              errors.destination_town
                                ? "ring-2 ring-red-500 rounded-lg"
                                : ""
                            }
                          >
                            <Dropdown
                              value={destination_town}
                              onChange={(e) => {
                                handleSelectChange({
                                  target: {
                                    id: "destination_town",
                                    value: e.value,
                                  },
                                });
                                setErrors((prev) => ({
                                  ...prev,
                                  destination_town: "",
                                }));
                                setShowValidationSummary(false);
                              }}
                              options={destination_towns}
                              optionLabel="name"
                              optionValue="id"
                              placeholder="Town *"
                              filter
                              className="w-full"
                              style={{ borderRadius: "0.5rem" }}
                            />
                          </div>
                          {errors.destination_town && (
                            <p className="text-red-600 text-xs mt-1">
                              {errors.destination_town}
                            </p>
                          )}
                        </div>
                      </div>
                    </>
                  )}

                  {/* Short Distance Form */}
                  {distance_type === "short" && (
                    <>
                      {/* Info Banner */}
                      <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                        <p className="text-sm text-purple-800">
                          <strong>Short Distance Delivery:</strong> Available
                          within 70km of our Midrand warehouse (Gallagher Business Exchange)
                        </p>
                      </div>

                      {/* Pickup Address */}
                      <AddressInput
                        id="departure_address"
                        name="departure_address"
                        value={departure_address}
                        onChange={(e) => {
                          setCreateOrderState((prevState) => ({
                            ...prevState,
                            departure_address: e.target.value,
                          }));
                          setErrors((prev) => ({
                            ...prev,
                            departure_address: "",
                          }));
                          setShowValidationSummary(false);
                          // Validate distance after a delay
                          setTimeout(() => {
                            validateAddressDistance(
                              e.target.value,
                              "departure",
                            );
                          }, 1000);
                        }}
                        onBlur={() => {
                          setAddressTouched((prev) => ({
                            ...prev,
                            departure_address: true,
                          }));
                          validateAddressDistance(
                            departure_address,
                            "departure",
                          );
                        }}
                        label="Pickup Address"
                        placeholder="Enter pickup address"
                        error={errors.departure_address}
                        touched={addressTouched.departure_address}
                        showRequired={true}
                      />

                      {/* Distance Warning for Pickup */}
                      {distanceWarning.departure && (
                        <div className="bg-orange-50 border-2 border-orange-500 rounded-lg p-3 animate-slideDown">
                          <div className="flex items-start gap-2">
                            <BsExclamationTriangleFill
                              className="text-orange-500 mt-0.5 flex-shrink-0"
                              size={16}
                            />
                            <p className="text-sm text-orange-800">
                              {distanceWarning.departure}
                            </p>
                          </div>
                        </div>
                      )}

                      {/* Delivery Address */}
                      <AddressInput
                        id="destination_address"
                        name="destination_address"
                        value={destination_address}
                        onChange={(e) => {
                          setCreateOrderState((prevState) => ({
                            ...prevState,
                            destination_address: e.target.value,
                          }));
                          setErrors((prev) => ({
                            ...prev,
                            destination_address: "",
                          }));
                          setShowValidationSummary(false);
                          // Validate distance after a delay
                          setTimeout(() => {
                            validateAddressDistance(
                              e.target.value,
                              "destination",
                            );
                          }, 1000);
                        }}
                        onBlur={() => {
                          setAddressTouched((prev) => ({
                            ...prev,
                            destination_address: true,
                          }));
                          validateAddressDistance(
                            destination_address,
                            "destination",
                          );
                        }}
                        label="Delivery Address"
                        placeholder="Enter delivery address"
                        error={errors.destination_address}
                        touched={addressTouched.destination_address}
                        showRequired={true}
                      />

                      {/* Distance Warning for Delivery */}
                      {distanceWarning.destination && (
                        <div className="bg-orange-50 border-2 border-orange-500 rounded-lg p-3 animate-slideDown">
                          <div className="flex items-start gap-2">
                            <BsExclamationTriangleFill
                              className="text-orange-500 mt-0.5 flex-shrink-0"
                              size={16}
                            />
                            <p className="text-sm text-orange-800">
                              {distanceWarning.destination}
                            </p>
                          </div>
                        </div>
                      )}
                    </>
                  )}

                  {/* Package Selection */}
                  <div id="package-section">
                    <label className="block text-sm font-semibold text-gray-700 mb-3">
                      Select Package Size{" "}
                      <span className="text-red-500">*</span>
                    </label>
                    {showPackageError && (
                      <div className="bg-red-50 border border-red-300 text-red-700 px-4 py-2 rounded-lg text-sm mb-3">
                        Please select a package type
                      </div>
                    )}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      {parcels &&
                        parcels.map((parcel, index) => (
                          <div
                            key={parcel.id}
                            className={`border-2 rounded-xl p-4 cursor-pointer transition-all ${
                              createOrderState.parcel_type === parcel.id
                                ? "border-purple-600 bg-purple-50"
                                : "border-gray-200 hover:border-purple-300"
                            }`}
                            onClick={() =>
                              handleRadioButtonChange({
                                name: "parcel_type",
                                value: parcel.id,
                              })
                            }
                          >
                            <div className="flex justify-between items-start mb-2">
                              <div className="font-semibold text-gray-800">
                                {parcel.package_type}
                              </div>
                              {createOrderState.parcel_type === parcel.id && (
                                <SlCheck
                                  size={20}
                                  className="text-purple-600"
                                />
                              )}
                            </div>
                            <div className="text-sm text-gray-600">
                              From R{parcel.parcel_prices[0].price.value}
                            </div>
                            <div className="text-xs text-gray-500 mt-1">
                              {parcel.max_height} x {parcel.max_width}{" "}
                              {parcel.max_weight}
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>

                  {/* Prohibited Items Checkbox */}
                  <div className="border-t border-gray-200 pt-3">
                    <div
                      className={`flex items-start gap-2 p-3 rounded-lg transition-all ${
                        errors.acceptedProhibitedItems
                          ? "bg-red-50 border border-red-400"
                          : "bg-gray-50 border border-gray-200"
                      }`}
                    >
                      <input
                        type="checkbox"
                        id="acceptedProhibitedItems"
                        checked={acceptedProhibitedItems}
                        onChange={(e) => {
                          setCreateOrderState((prevState) => ({
                            ...prevState,
                            acceptedProhibitedItems: e.target.checked,
                          }));
                          setErrors((prev) => ({
                            ...prev,
                            acceptedProhibitedItems: "",
                          }));
                          setShowValidationSummary(false);
                        }}
                        className="mt-0.5 h-4 w-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500 cursor-pointer"
                      />
                      <label
                        htmlFor="acceptedProhibitedItems"
                        className="flex-1 cursor-pointer text-xs text-gray-700"
                      >
                        I confirm I'm not sending prohibited items (weapons,
                        explosives, illegal substances, etc.){" "}
                        <span className="text-red-500">*</span>
                      </label>
                    </div>
                    {errors.acceptedProhibitedItems && (
                      <p className="text-red-600 text-xs mt-1.5 ml-1">
                        {errors.acceptedProhibitedItems}
                      </p>
                    )}
                  </div>

                  <button
                    type="submit"
                    className="w-full bg-gradient-to-r from-purple-600 to-violet-600 hover:from-purple-700 hover:to-violet-700 text-white font-bold py-4 rounded-xl transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-2 group"
                  >
                    Get Quote & Continue
                    <BsArrowRight
                      size={18}
                      className="transition-transform group-hover:translate-x-1"
                    />
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Features Banner */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 py-8">
        <div className="max-w-6xl mx-auto px-5">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 text-white">
            <div className="flex items-center gap-3">
              <BsTruck className="text-3xl flex-shrink-0" />
              <div>
                <div className="font-semibold">Fast Delivery</div>
                <div className="text-sm opacity-90">Same-day available</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <BsShieldCheck className="text-3xl flex-shrink-0" />
              <div>
                <div className="font-semibold">100% Insured</div>
                <div className="text-sm opacity-90">Your parcels protected</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="text-3xl flex-shrink-0">🌍</div>
              <div>
                <div className="font-semibold">Eco-Friendly</div>
                <div className="text-sm opacity-90">Sustainable delivery</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <BsClock className="text-3xl flex-shrink-0" />
              <div>
                <div className="font-semibold">Real-Time Tracking</div>
                <div className="text-sm opacity-90">
                  Always know where it is
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* How It Works */}
      <div className="bg-white py-16">
        <div className="max-w-6xl mx-auto px-5">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-purple-800 mb-4">
              How It Works
            </h2>
            <p className="text-gray-600 text-lg">Simple, fast, and reliable</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-4">
                1
              </div>
              <h3 className="text-xl font-semibold mb-2">Enter Details</h3>
              <p className="text-gray-600">
                Select pickup and delivery locations, choose package size
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-4">
                2
              </div>
              <h3 className="text-xl font-semibold mb-2">Book & Pay</h3>
              <p className="text-gray-600">
                Get instant quote, confirm booking and make secure payment
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-4">
                3
              </div>
              <h3 className="text-xl font-semibold mb-2">Track & Receive</h3>
              <p className="text-gray-600">
                Track in real-time and receive your parcel safely
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Trusted Clients — only shown when clients are configured */}
      {trustedClients.length > 0 && (
        <div className="bg-gray-50 py-14 border-t border-gray-100">
          <div className="max-w-6xl mx-auto px-5">
            <div className="text-center mb-10">
              <p className="text-sm font-semibold uppercase tracking-widest text-purple-500 mb-2">
                Trusted By
              </p>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-800">
                Brands That Trust Parcelin
              </h2>
            </div>
            {trustedClients.length <= 5 ? (
              <div className="flex flex-wrap justify-center items-center gap-8">
                {trustedClients.map((client, i) => (
                  <ClientCard key={i} client={client} />
                ))}
              </div>
            ) : (
              <div className="overflow-hidden">
                <style>{`
                  @keyframes marquee {
                    0% { transform: translateX(0); }
                    100% { transform: translateX(-50%); }
                  }
                  .marquee-track { animation: marquee 30s linear infinite; }
                  .marquee-track:hover { animation-play-state: paused; }
                `}</style>
                <div
                  className="flex marquee-track"
                  style={{ width: "max-content" }}
                >
                  {[...trustedClients, ...trustedClients].map((client, i) => (
                    <div key={i} className="mx-4">
                      <ClientCard client={client} />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Customer Reviews */}
      <div className="bg-white py-16">
        <div className="max-w-6xl mx-auto px-5">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-purple-800 mb-4">
              What Our Customers Say
            </h2>
            <p className="text-gray-600 text-lg">
              Join thousands of satisfied customers across South Africa
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                  <span className="text-purple-600 font-bold text-lg">NM</span>
                </div>
                <div>
                  <div className="font-semibold text-gray-800">
                    Ntsakisi Manganyi
                  </div>
                  <div className="flex gap-1">
                    {[...Array(5)].map((_, i) => (
                      <span key={i} className="text-yellow-400">
                        ★
                      </span>
                    ))}
                  </div>
                </div>
              </div>
              <p className="text-gray-600 text-sm">
                Finally here, been waiting for this for a long time since I have
                been sending my parcels through Liftsearch. Tracking makes it
                more worthwhile.
              </p>
            </div>

            <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                  <span className="text-purple-600 font-bold text-lg">DM</span>
                </div>
                <div>
                  <div className="font-semibold text-gray-800">
                    Desmond Makhubele
                  </div>
                  <div className="flex gap-1">
                    {[...Array(5)].map((_, i) => (
                      <span key={i} className="text-yellow-400">
                        ★
                      </span>
                    ))}
                  </div>
                </div>
              </div>
              <p className="text-gray-600 text-sm">
                Really cool innovations, I use Liftsearch and Parcelin for all
                my logistics.
              </p>
            </div>

            <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                  <span className="text-purple-600 font-bold text-lg">NM</span>
                </div>
                <div>
                  <div className="font-semibold text-gray-800">
                    Nissan Maluleke
                  </div>
                  <div className="flex gap-1">
                    {[...Array(5)].map((_, i) => (
                      <span key={i} className="text-yellow-400">
                        ★
                      </span>
                    ))}
                  </div>
                </div>
              </div>
              <p className="text-gray-600 text-sm">
                I like the fact that its straight to the point and hassle free
              </p>
            </div>

            <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                  <span className="text-purple-600 font-bold text-lg">SM</span>
                </div>
                <div>
                  <div className="font-semibold text-gray-800">
                    Shongi Mukhari
                  </div>
                  <div className="flex gap-1">
                    {[...Array(5)].map((_, i) => (
                      <span key={i} className="text-yellow-400">
                        ★
                      </span>
                    ))}
                  </div>
                </div>
              </div>
              <p className="text-gray-600 text-sm">
                Helps with delivering Archaar and Bones to my clients in
                Limpopo. Very worth it.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Service Level Agreements */}
      <div className="bg-white py-16">
        <div className="max-w-6xl mx-auto px-5">
          <div className="text-center mb-10">
            <span className="text-purple-500 text-xs font-bold uppercase tracking-widest">
              Delivery Options
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-2 mb-3">
              Service Level Agreements
            </h2>
            <p className="text-gray-500 text-lg max-w-xl mx-auto">
              Clear timelines so you always know what to expect
            </p>
          </div>

          <div className="max-w-3xl mx-auto flex flex-col gap-4">
            {/* Same Day */}
            <Disclosure>
              {({ open }) => (
                <div
                  className={`rounded-2xl border-2 overflow-hidden shadow-sm transition-shadow duration-300 ${open ? "border-emerald-300 shadow-emerald-100" : "border-gray-100"}`}
                >
                  <Disclosure.Button className="w-full text-left px-6 py-5 flex items-center justify-between gap-4 bg-white hover:bg-gray-50 transition-colors">
                    <div className="flex items-center gap-4">
                      <div
                        className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors ${open ? "bg-emerald-500" : "bg-emerald-100"}`}
                      >
                        <BsLightningCharge
                          size={20}
                          className={open ? "text-white" : "text-emerald-600"}
                        />
                      </div>
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-bold text-gray-900">
                            Same Day Delivery
                          </span>
                          <span className="bg-emerald-100 text-emerald-700 text-[11px] font-bold px-2 py-0.5 rounded-full">
                            Fastest
                          </span>
                        </div>
                        <p className="text-sm text-gray-500 mt-0.5">
                          Pay before 9am · Arrives by 5pm same day
                        </p>
                      </div>
                    </div>
                    <ChevronDownIcon
                      className={`w-5 h-5 text-gray-400 flex-shrink-0 transition-transform duration-200 ${open ? "rotate-180 text-emerald-500" : ""}`}
                    />
                  </Disclosure.Button>
                  <Transition
                    show={open}
                    enter="transition duration-200 ease-out"
                    enterFrom="opacity-0 -translate-y-1"
                    enterTo="opacity-100 translate-y-0"
                    leave="transition duration-150 ease-in"
                    leaveFrom="opacity-100 translate-y-0"
                    leaveTo="opacity-0 -translate-y-1"
                  >
                    <Disclosure.Panel
                      static
                      className="border-t border-emerald-100 bg-emerald-50/50 px-6 py-5"
                    >
                      <ul className="space-y-3">
                        {[
                          "Pay order before 9am of Delivery Date",
                          "Collected during office hours, within 3 hours of payment",
                          "Reaches destination by 5pm same day",
                          "Limpopo: collected in town · Gauteng: door drop within 70km of Our Midrand Warehouse",
                        ].map((item, i) => (
                          <li key={i} className="flex items-start gap-2.5">
                            <BsCheckCircle
                              className="text-emerald-500 mt-0.5 flex-shrink-0"
                              size={15}
                            />
                            <span className="text-sm text-gray-700">
                              {item}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </Disclosure.Panel>
                  </Transition>
                </div>
              )}
            </Disclosure>

            {/* Overnight */}
            <Disclosure>
              {({ open }) => (
                <div
                  className={`rounded-2xl border-2 overflow-hidden shadow-sm transition-shadow duration-300 ${open ? "border-violet-300 shadow-violet-100" : "border-gray-100"}`}
                >
                  <Disclosure.Button className="w-full text-left px-6 py-5 flex items-center justify-between gap-4 bg-white hover:bg-gray-50 transition-colors">
                    <div className="flex items-center gap-4">
                      <div
                        className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors ${open ? "bg-violet-600" : "bg-violet-100"}`}
                      >
                        <BsMoonStars
                          size={18}
                          className={open ? "text-white" : "text-violet-600"}
                        />
                      </div>
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-bold text-gray-900">
                            Overnight Delivery
                          </span>
                          <span className="bg-violet-100 text-violet-700 text-[11px] font-bold px-2 py-0.5 rounded-full">
                            Most Popular
                          </span>
                        </div>
                        <p className="text-sm text-gray-500 mt-0.5">
                          Pay before 2pm · Arrives by 8am next day
                        </p>
                      </div>
                    </div>
                    <ChevronDownIcon
                      className={`w-5 h-5 text-gray-400 flex-shrink-0 transition-transform duration-200 ${open ? "rotate-180 text-violet-500" : ""}`}
                    />
                  </Disclosure.Button>
                  <Transition
                    show={open}
                    enter="transition duration-200 ease-out"
                    enterFrom="opacity-0 -translate-y-1"
                    enterTo="opacity-100 translate-y-0"
                    leave="transition duration-150 ease-in"
                    leaveFrom="opacity-100 translate-y-0"
                    leaveTo="opacity-0 -translate-y-1"
                  >
                    <Disclosure.Panel
                      static
                      className="border-t border-violet-100 bg-violet-50/50 px-6 py-5"
                    >
                      <ul className="space-y-3">
                        {[
                          "Pay order before 2pm of Pickup Date",
                          "Collected during office hours, within 1 hour of payment",
                          "Reaches destination by 8am the next day",
                          "Limpopo: collected in town · Gauteng: door drop within 70km of Our Midrand Warehouse",
                        ].map((item, i) => (
                          <li key={i} className="flex items-start gap-2.5">
                            <BsCheckCircle
                              className="text-violet-500 mt-0.5 flex-shrink-0"
                              size={15}
                            />
                            <span className="text-sm text-gray-700">
                              {item}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </Disclosure.Panel>
                  </Transition>
                </div>
              )}
            </Disclosure>

            {/* Next Friday */}
            <Disclosure>
              {({ open }) => (
                <div
                  className={`rounded-2xl border-2 overflow-hidden shadow-sm transition-shadow duration-300 ${open ? "border-amber-300 shadow-amber-100" : "border-gray-100"}`}
                >
                  <Disclosure.Button className="w-full text-left px-6 py-5 flex items-center justify-between gap-4 bg-white hover:bg-gray-50 transition-colors">
                    <div className="flex items-center gap-4">
                      <div
                        className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors ${open ? "bg-amber-500" : "bg-amber-100"}`}
                      >
                        <BsCalendarCheck
                          size={18}
                          className={open ? "text-white" : "text-amber-600"}
                        />
                      </div>
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-bold text-gray-900">
                            Next Friday Delivery
                          </span>
                          <span className="bg-amber-100 text-amber-700 text-[11px] font-bold px-2 py-0.5 rounded-full">
                            Best Value
                          </span>
                        </div>
                        <p className="text-sm text-gray-500 mt-0.5">
                          Pay before 4pm Thursday · Arrives Saturday 1pm
                        </p>
                      </div>
                    </div>
                    <ChevronDownIcon
                      className={`w-5 h-5 text-gray-400 flex-shrink-0 transition-transform duration-200 ${open ? "rotate-180 text-amber-500" : ""}`}
                    />
                  </Disclosure.Button>
                  <Transition
                    show={open}
                    enter="transition duration-200 ease-out"
                    enterFrom="opacity-0 -translate-y-1"
                    enterTo="opacity-100 translate-y-0"
                    leave="transition duration-150 ease-in"
                    leaveFrom="opacity-100 translate-y-0"
                    leaveTo="opacity-0 -translate-y-1"
                  >
                    <Disclosure.Panel
                      static
                      className="border-t border-amber-100 bg-amber-50/50 px-6 py-5"
                    >
                      <ul className="space-y-3">
                        {[
                          "Pay order before 4pm, Thursday",
                          "Collected during office hours, within 3 hours of payment",
                          "Reaches destination by next Saturday at 1pm",
                          "Limpopo: collected in town · Gauteng: door drop within 70km of Our Midrand Warehouse",
                        ].map((item, i) => (
                          <li key={i} className="flex items-start gap-2.5">
                            <BsCheckCircle
                              className="text-amber-500 mt-0.5 flex-shrink-0"
                              size={15}
                            />
                            <span className="text-sm text-gray-700">
                              {item}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </Disclosure.Panel>
                  </Transition>
                </div>
              )}
            </Disclosure>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-gradient-to-br from-purple-600 to-indigo-600 py-16">
        <div className="max-w-4xl mx-auto px-5 text-center text-white">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Ready to Send Your Parcel?
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Get started in minutes with our simple booking process
          </p>
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
            className="inline-block bg-white text-purple-600 px-8 py-4 rounded-xl font-bold text-lg hover:bg-gray-100 transition-all shadow-lg"
          >
            Get Started Now
          </a>
        </div>
      </div>
    </div>
  );
};
